'use client';

/**
 * "Remix with" dialog — opened from a widget's Remix pill.
 *
 *  • Claude Code / Codex / Antigravity / Cursor → make a private copy, then fire that tool's
 *    deep-link with a ready remix prompt (connect the Robutler MCP, read /REMIX.md, edit the copy
 *    with the MCP tools). A copy-the-prompt fallback covers tools that aren't installed / register
 *    no handler.
 *  • Robutler agent → remix mode only (see `visibleEnvs`). Hidden on 2026-08-27,
 *    back by owner call on 2026-09-30 as the SMALL-change path: the row is listed
 *    first, the private copy lands on the canvas and the person's own agent opens
 *    prefilled to tweak it (`onRobutler`, which is `doRemix` in content-node and
 *    `onRobutlerRemix` in the standalone viewer). Large changes and rebuilds stay
 *    with the coding tools, and the agent's build-apps skill says so itself
 *    (`SCOPE_RULE` in lib/agents/portal-build-apps-skill.ts). Edit mode keeps the
 *    tools only: both edit-mode mount sites (apps-tab, draft-app-panel) pass a
 *    no-op `onRobutler`, and the remix handoffs MINT A COPY, which is the one
 *    thing editing your own app in place must never do.
 *
 * Deep-link schemes (official docs):
 *   Claude Code  claude://code/new?q=…   — Claude DESKTOP's Code session (support.claude.com
 *                /en/articles/14729294). NOT `claude-cli://open` (the terminal CLI's scheme):
 *                that handler only exists after running an interactive `claude` ≥ v2.1.91,
 *                so it silently no-ops on most machines — the Desktop scheme is the reliable one.
 *   Cursor       cursor://anysphere.cursor-deeplink/prompt?text=…   cursor.com/docs/integrations/deeplinks
 *   Codex        codex://threads/new?prompt=…                developers.openai.com/codex/app/commands
 *   Antigravity  antigravity://…  (best-effort; clipboard fallback if it doesn't carry the prompt)
 */

import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink, Loader2, X } from 'lucide-react';
import { appLinksNeedSynchronousClick, launchCodingEnv } from './coding-env-marks';

export interface PrivateCopy {
  contentId: string;
  folderId: string | null;
  displayName: string;
  /** Live instance the copy was dropped onto, so the agent can drive +
   *  inspect it (workspace_widgets_*) once the user has the tab open. */
  workspaceId?: string;
  itemId?: string;
  /** False when the account cannot deploy yet (widget_publish /
   *  widget_snapshot are cohort-gated and UNREGISTERED for it), so the
   *  prompt must not send the agent to tools it does not have. */
  canPublish?: boolean;
}

/** Failure channel for `createPrivateCopy`: the server's stated reason (an
 *  ACL refusal, quota, size cap) — shown in the dialog instead of the generic
 *  "try again" line, which mislabels permanent refusals as transient. */
export interface PrivateCopyError {
  error: string;
}

interface RemixWithDialogProps {
  open: boolean;
  onClose: () => void;
  widgetName: string;
  /**
   * 'remix' (default): fork someone's app into a private copy, then edit (REMIX.md).
   * 'edit': edit YOUR OWN app in place, no copy (EDIT.md). Pass `editTarget`.
   */
  mode?: 'remix' | 'edit';
  /** Run the existing in-app factory flow (Robutler option). */
  onRobutler: () => void;
  /** Remix mode: create the editable private copy; returns its ids, the
   *  server's refusal reason, or null on a transport-level failure. */
  createPrivateCopy?: () => Promise<PrivateCopy | PrivateCopyError | null>;
  /** Edit mode: the owner's existing app to edit in place (no copy). */
  editTarget?: PrivateCopy;
}

/* ---- official brand marks (single-path, 24×24, rendered white on a brand chip) ---------------- */
const PATHS = {
  // OpenAI (Codex)
  openai:
    'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z',
  // Claude (Claude Code)
  claude:
    'm4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z',
  // Cursor
  cursor:
    'M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23',
  // Antigravity
  antigravity:
    'M21.751 22.607c1.34 1.005 3.35.335 1.508-1.508C17.73 15.74 18.904 1 12.037 1 5.17 1 6.342 15.74.815 21.1c-2.01 2.009.167 2.511 1.507 1.506 5.192-3.517 4.857-9.714 9.715-9.714 4.857 0 4.522 6.197 9.714 9.715z',
} as const;

function Mark({ d, className }: { d: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

type EnvId = 'robutler' | 'claude' | 'codex' | 'antigravity' | 'cursor';

interface EnvDef {
  id: EnvId;
  name: string;
  sub: string;
  chip: string; // brand chip background
  logo: ReactNode;
  /** Build the deep link from a URL-encoded prompt (omitted for Robutler). */
  scheme?: (encodedPrompt: string) => string;
}

const ENVS: EnvDef[] = [
  {
    id: 'robutler',
    name: 'Robutler agent',
    sub: 'For small changes, right here',
    chip: 'bg-[linear-gradient(135deg,#6366f1_0%,#a855f7_45%,#ec4899_75%,#f97316_100%)]',
    logo: <img src="/icons/Logo_W.svg" alt="" className="h-4 w-4" />,
  },
  {
    id: 'claude',
    name: 'Claude Code',
    sub: 'Opens a Code session in Claude Desktop',
    chip: 'bg-[#D97757]',
    logo: <Mark d={PATHS.claude} className="h-4 w-4 text-white" />,
    scheme: (q) => `claude://code/new?q=${q}`,
  },
  {
    id: 'codex',
    name: 'Codex',
    sub: 'Opens a new Codex thread',
    chip: 'bg-black',
    logo: <Mark d={PATHS.openai} className="h-4 w-4 text-white" />,
    scheme: (q) => `codex://threads/new?prompt=${q}`,
  },
  {
    id: 'antigravity',
    name: 'Antigravity',
    sub: 'Opens Google Antigravity',
    chip: 'bg-[#4285F4]',
    logo: <Mark d={PATHS.antigravity} className="h-4 w-4 text-white" />,
    scheme: (q) => `antigravity://prompt?text=${q}`,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    sub: 'Opens Cursor with the prompt',
    chip: 'bg-black',
    logo: <Mark d={PATHS.cursor} className="h-4 w-4 text-white" />,
    scheme: (q) => `cursor://anysphere.cursor-deeplink/prompt?text=${q}`,
  },
];

/**
 * The rows the dialog offers, by mode. Remix lists the Robutler agent FIRST,
 * then the coding tools (owner call, 2026-09-30: the in-app agent takes on
 * small remixes; it had been hidden everywhere since 2026-08-27). Edit mode
 * stays tools-only: its mount sites pass a no-op `onRobutler`, and the only
 * Robutler handoffs that exist fork a private copy, which editing your own
 * app in place must not do.
 */
export function visibleEnvs(mode: 'remix' | 'edit'): EnvDef[] {
  return mode === 'remix' ? ENVS : ENVS.filter((e) => e.id !== 'robutler');
}

/** The last instruction depends on what the account can do: `widget_publish`
 *  and `widget_snapshot` are cohort-gated and simply absent from the MCP tool
 *  list outside the cohort, so pointing an agent at them ends the session in
 *  a dead end. */
export function deployLine(copy: PrivateCopy): string {
  return copy.canPublish === false
    ? 'Publishing is not enabled on my account yet, so do not call widget_publish or widget_snapshot; preview the edited bundle through widget_download plus the local dev host (widget_dev_token) and the live canvas instance instead.'
    : 'Deploy with widget_publish and cut a version with widget_snapshot.';
}

function buildPrompt(copy: PrivateCopy, widgetName: string, mode: 'remix' | 'edit' = 'remix'): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://robutler.ai';
  if (mode === 'edit') {
    const lines = [
      `Edit my Robutler app "${copy.displayName || widgetName}" in place. It is mine, no copy.`,
      '',
      `1) Connect the Robutler MCP server (Streamable HTTP, OAuth: authorize as the owner): ${origin}/mcp`,
      `2) Read the edit guide: ${origin}/EDIT.md`,
      `3) The app's bundle folder id: ${copy.folderId ?? copy.contentId} (widget id: ${copy.contentId}).`,
    ];
    if (copy.workspaceId && copy.itemId) {
      lines.push(
        `4) It is live on my canvas (workspace ${copy.workspaceId}, item ${copy.itemId}). With that tab open you can drive + inspect it: workspace_widgets_list, workspace_widgets_invoke (e.g. __screenshot, __getState), workspace_widgets_logs.`,
      );
    }
    lines.push(
      '',
      `Read EDIT.md, then the bundle's AGENT.md or README (via widget_download); edit files with widget_put_files, then ask me what to change. ${deployLine(copy)}`,
    );
    return lines.join('\n');
  }
  const lines = [
    `Remix the Robutler widget "${copy.displayName || widgetName}" for me.`,
    '',
    `1) Connect the Robutler MCP server (Streamable HTTP, OAuth: authorize when prompted): ${origin}/mcp`,
    `2) Read the remix guide: ${origin}/REMIX.md`,
    `3) I made you a private copy to edit. Widget id: ${copy.contentId}${copy.folderId ? `, bundle folder: ${copy.folderId}` : ''}.`,
  ];
  if (copy.workspaceId && copy.itemId) {
    lines.push(
      `4) It is already live on my canvas (workspace ${copy.workspaceId}, item ${copy.itemId}). While I keep that tab open and in the foreground you can drive and inspect it: workspace_widgets_list, workspace_widgets_invoke (e.g. __screenshot, __getState), and workspace_widgets_logs for its console errors. If workspace_list_open does not show it, ask me to bring the tab forward.`,
    );
  }
  lines.push(
    '',
    `Read REMIX.md, then the bundle's AGENT.md or README if it ships one (via widget_download); otherwise orient from widget.json plus the entry HTML. Edit files with widget_put_files, then ask me what to change. ${deployLine(copy)}`,
  );
  return lines.join('\n');
}

export function RemixWithDialog({ open, onClose, widgetName, mode = 'remix', onRobutler, createPrivateCopy, editTarget }: RemixWithDialogProps) {
  const t = useT();
  const [busy, setBusy] = useState<EnvId | null>(null);
  /** `ready`: the copy exists but the tool was NOT opened, because the click's
   *  gesture did not survive the await (WebKit); the Open button does it. */
  const [panel, setPanel] = useState<{ env: EnvDef; prompt: string; url: string; ready?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Remix confirmation: picking an env SELECTS it; the copy is only minted
  // on an explicit "Create remix". Remixing looks like browsing but has a
  // side effect (a new owned app in My Library), so the row click must not
  // be the commit. Edit mode changes nothing it doesn't own — no copy, no
  // confirm step.
  const [confirmEnv, setConfirmEnv] = useState<EnvDef | null>(null);

  // Reset transient state whenever the dialog (re)opens.
  useEffect(() => {
    if (open) { setPanel(null); setBusy(null); setCopied(false); setErr(null); setConfirmEnv(null); }
  }, [open]);

  /** Fire the tool's deep link and copy the prompt, both inside the click that
   *  called this. Deep link first: see `launchCodingEnv` for the Safari
   *  failure the old copy-then-open order produced. The clipboard makes every
   *  outcome recoverable with a single paste: Antigravity opens without the
   *  prompt, an uninstalled tool opens nothing. */
  const launch = useCallback((env: EnvDef, prompt: string) => {
    const launched = launchCodingEnv(env, prompt);
    if (!launched) return null;
    void launched.copied.then((ok) => { if (ok) setCopied(true); });
    return launched;
  }, []);

  const execute = useCallback(async (env: EnvDef) => {
    if (env.id === 'robutler') { onRobutler(); onClose(); return; }
    if (!env.scheme || busy) return;
    setBusy(env.id); setErr(null);
    // Edit mode targets the owner's existing app in place; remix forks a copy.
    const copy = mode === 'edit' ? (editTarget ?? null) : (createPrivateCopy ? await createPrivateCopy() : null);
    setBusy(null);
    if (copy && 'error' in copy) { setErr(copy.error); return; }
    if (!copy) { setErr(mode === 'edit' ? t('workspace.remixWith.noAppToEdit') : t('workspace.remixWith.copyFailed')); return; }
    const prompt = buildPrompt(copy, widgetName, mode);
    // Remix mode has just AWAITED the private copy, and on WebKit the click's
    // gesture does not survive that: a deep link fired now ends in Safari's
    // "address is invalid" alert (`launchCodingEnv`). There the panel opens in
    // its ready state and its Open button fires the link from a fresh click;
    // engines that keep a transient activation for a few seconds open the
    // tool right away, as before. Edit mode never awaited, so it launches
    // here everywhere.
    if (mode === 'remix' && appLinksNeedSynchronousClick()) {
      setPanel({ env, prompt, url: env.scheme(encodeURIComponent(prompt)), ready: true });
      return;
    }
    const launched = launch(env, prompt);
    if (launched) setPanel({ env, prompt, url: launched.url });
  }, [busy, mode, editTarget, createPrivateCopy, launch, onClose, onRobutler, widgetName]);

  const pick = useCallback(
    (env: EnvDef) => {
      // Remix mints a copy — hold for the explicit confirm. Edit targets the
      // owner's existing app in place, so the row click stays the action.
      if (mode === 'remix') { setConfirmEnv(env); setErr(null); return; }
      void execute(env);
    },
    [mode, execute],
  );

  const copyPrompt = useCallback(async () => {
    if (!panel) return;
    try { await navigator.clipboard.writeText(panel.prompt); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  }, [panel]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-sm font-semibold">
            {`${mode === 'edit' ? t('user.editAvatar') : t('widget.remix')} with${panel ? ` ${panel.env.name}` : ''}`}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('workspace.canvas.node.close')}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!panel && confirmEnv ? (
          <div className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${confirmEnv.chip}`}>
                {confirmEnv.logo}
              </span>
              <p className="min-w-0 text-sm font-medium">
                Remix &ldquo;{widgetName}&rdquo; with {confirmEnv.name}?
              </p>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('workspace.remixWith.intro')}
            </p>
            {err && <p className="text-xs text-red-500">{err}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmEnv(null)}
                disabled={!!busy}
                className="inline-flex flex-1 items-center justify-center rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-accent disabled:opacity-60"
              >
                Back
              </button>
              <button
                onClick={() => { void execute(confirmEnv); }}
                disabled={!!busy}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background hover:opacity-90 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Create remix
              </button>
            </div>
          </div>
        ) : !panel ? (
          <div className="p-2">
            {visibleEnvs(mode).map((env) => (
              <button
                key={env.id}
                onClick={() => void pick(env)}
                disabled={!!busy}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent disabled:opacity-60"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${env.chip}`}>
                  {env.logo}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{env.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{env.sub}</span>
                </span>
                {busy === env.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                ) : env.id !== 'robutler' ? (
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : null}
              </button>
            ))}
            {err && <p className="px-3 py-2 text-xs text-red-500">{err}</p>}
          </div>
        ) : (
          <div className="space-y-3 p-4">
            <p className="text-xs text-muted-foreground">
              {panel.ready ? (
                <>{t('workspace.remixWith.readyOpen', { env: panel.env.name })}</>
              ) : (
                <>Opening <span className="font-medium text-foreground">{panel.env.name}</span>…</>
              )}
              {copied ? ' The prompt is in your clipboard. If it opened without it, just paste.' : ''}{' '}
              If nothing happens, make sure it's installed, then paste the prompt after connecting the MCP.
            </p>
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-foreground">
              {panel.prompt}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={() => void copyPrompt()}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-accent"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('settings.copied') : t('workspace.createWith.copyPrompt')}
              </button>
              <button
                onClick={() => launch(panel.env, panel.prompt)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background hover:opacity-90"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open {panel.env.name}
              </button>
            </div>
            <button onClick={() => setPanel(null)} className="w-full text-center text-xs text-muted-foreground hover:text-foreground">
              {t('workspace.chooseDifferentEnv')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
